import renderInputField from 'components/FormControls/textInput'
import { Button } from 'gu-ui-kit'
import omit from 'lodash/omit'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Field, formValueSelector, reduxForm } from 'redux-form'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'
import baseLoginValidator from 'utils/validators/baseLoginValidator'
import * as Yup from 'yup'
import initialAdvertiserInfo from 'modules/Forms/Account/Create/initialAdvertiserInfo'
import advertiserInfoValidationRulesExtended from 'modules/Forms/Account/Create/subcomponents/advertiser-info-validation-rules-extended'
import LoginField from '../subcomponents/LoginField'
import { useAccountCreateFormSubmit } from 'modules/Forms/Account/Create/useAccountCreateFormSubmit'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { ROUTES } from 'constants/routes'
import FormFrame from 'modules/Forms/Account/Create/subcomponents/FormFrame'
import { FETCH_NETWORK_SETTINGS } from 'modules/Services/types'
import clsx from 'clsx'
import formStyles from 'modules/Forms/common-form.module.scss'

export const AdsmarketSchema = () =>
  Yup.object()
    .shape({
      logins: Yup.array().of(Yup.object()),
      login: Yup.string().when('logins', ([logins], schema) =>
        logins
          ? schema.strip()
          : schema
              .test('baseLoginTest', '', baseLoginValidator)
              .required('common::validation_messages.required'),
      ),
      network: Yup.string().required('common::validation_messages.required'),
      vk_profile_url: Yup.string().required('common::validation_messages.required'),
      vk_token: Yup.string(),
    })
    .concat(advertiserInfoValidationRulesExtended())

let CreateAdsMarketAccountForm = ({
  handleSubmit,
  submitting,
  onClose,
  autofill,
  networkType,
  form,
  networkId,
  array,
  multiLogins,
  submitFailed,
}) => {
  let childWindow
  const pathGen = useGeneratePathI18n()
  const selector = formValueSelector(form)
  const { t } = useTranslation([])
  const submitAccountData = useAccountCreateFormSubmit({ form, onClose, multiLogins, networkType })
  const dispatch = useDispatch()

  const {
    i18n,
    formValues: { vk_profile_url, advertiser_info },
    settings = null,
  } = useSelector((state) => ({
    i18n: state['app'].i18n,
    formValues: selector(state, 'vk_profile_url', 'advertiser_info'),
    settings: state['services/account-create'].settings,
  }))

  global.getBack = useCallback(
    ({ profile_url, access_token }) => {
      !!childWindow && childWindow.close()
      childWindow = null

      autofill('vk_profile_url', profile_url)
      autofill('vk_token', access_token)
    },
    [childWindow, autofill],
  )

  useEffect(() => {
    if (networkId) {
      autofill('network', networkId)

      dispatch({
        type: FETCH_NETWORK_SETTINGS,
        payload: { networkId },
      })
    }
  }, [networkId])

  const submit = useCallback(
    (values) => submitAccountData(omit(values, ['advertiser_template'])),
    [submitAccountData],
  )

  const openConnectWindow = useCallback(() => {
    childWindow = global.open(
      pathGen(ROUTES.ADSMARKET_AUTH),
      'Vk oAuth dialog',
      'resizable,toolbar=yes,location=yes,scrollbars=yes,menubar=yes,width=800,height=600,top=0,left=0',
    )
  }, [childWindow, i18n])

  useEffect(() => {
    return () => {
      global.getBack = undefined
    }
  }, [])

  return (
    <FormFrame
      onSubmit={handleSubmit(submit)}
      multiLogins={multiLogins}
      form={form}
      advertiser_info={advertiser_info}
      submitFailed={submitFailed}
      submitAccountData={submitAccountData}
      submitting={submitting}
      onClose={onClose}
    >
        <Field
          disabled
          name="network"
          hidden
          fullWidth
          component={renderInputField}
          t={t}
        />

        <div className={clsx('margin-bottom-16', formStyles['row_with-tooltip'])}>
          <LoginField
            form={form}
            multiLogins={multiLogins}
            array={array}
            autofill={autofill}
            networkId={networkId}
            label={'services::forms.register_account.adsmarket.fields.login.label'}
          />
        </div>

        <div className={clsx('margin-bottom-48', formStyles['row_with-tooltip'])}>
          <Field
            name="vk_profile_url"
            fullWidth
            component={renderInputField}
            label={t('services::forms.register_account.adsmarket.fields.vk_profile_url.label')}
            readOnly={!!settings?.user_can_create_multiple_accounts === false}
            t={t}
            InputProps={{
              endAdornment: !vk_profile_url &&
                !!settings?.user_can_create_multiple_accounts === false && (
                  <Button
                    variant={'link'}
                    onClick={openConnectWindow}
                    color={'Label-GU-Primary'}
                    noWrap
                  >
                    {t(
                      'services::forms.register_account.adsmarket.fields.vk_profile_url.get_url_label',
                    )}
                  </Button>
                ),
            }}
          />
        </div>
        <Field
          hidden
          name="vk_token"
          fullWidth
          component={renderInputField}
          label={t('services::forms.register_account.adsmarket.fields.vk_token.label')}
          t={t}
        />

    </FormFrame>
  )
}

CreateAdsMarketAccountForm = reduxForm({
  form: 'CreateAdsMarketAccountForm',
  initialValues: {
    login: '',
    network: '',
    vk_profile_url: '',
    vk_token: '',
    payment_profile_id: undefined,
    advertiser_info: initialAdvertiserInfo,
  },
  shouldAsyncValidate: shouldAsyncValidate,
})(CreateAdsMarketAccountForm)

CreateAdsMarketAccountForm.propTypes = {
  networkId: PropTypes.string,
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  onClose: PropTypes.func,
  valid: PropTypes.bool,
  pristine: PropTypes.bool,
  autofill: PropTypes.func,
  change: PropTypes.func,
  networkType: PropTypes.string,
  form: PropTypes.string,
  multiLogins: PropTypes.bool,
  array: PropTypes.object,
  initialValues: PropTypes.object,
  submitFailed: PropTypes.bool,
}

export default CreateAdsMarketAccountForm
